<template>
	<Dialog
		:dialog-visible="dialog && !isAddressLookupOpen"
		:title="isEditing ? 'Edit Project' : 'Create Project'"
		:agree-text="agreeText"
		cancel-text="Cancel"
		:disable-agree-button="createButtonDisabled"
		:agree-loading="creating"
		@cancel="cancel"
		@agree="agree">
		<template #dialog-body>
			<div v-if="loading" class="d-flex justify-center pa-4">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<v-form v-else ref="formRef" v-model="isFormValid">
				<!-- field-noun="name" below is deliberately not "project name": this dialog's only
				     name-like field is labeled "Name (Required)" (unambiguous, single field).
				     AddressLookup.vue's equivalent field uses field-noun="project name" because THAT
				     form shows a Project Name and a Project Address field together, where "name" alone
				     would be ambiguous. Review finding: not a copy inconsistency to reconcile — each
				     matches its own on-screen label. -->
				<CharacterLimitedTextField
					v-model.trim="project.name"
					v-test="'create-new-project-name'"
					id="project-name"
					field-noun="name"
					:max-length="255"
					:rules="nameRules"
					:autofocus="shouldAutoFocusProjectName"
					@keyup.enter="createButtonDisabled ? null : agree()">
					<template #label><FieldLabel label="Name (Required)" /></template>
				</CharacterLimitedTextField>
				<!-- LSP-18474: web parity with the native Create Project sheet, which offers
				     "Use my location". Reuses the same link/picker pair the Action* cards use so the
				     entry point reads identically everywhere location is supported. Hidden while
				     editing: the picker's job is finding or creating a project, not renaming one. -->
				<AddressLookupLink v-if="!isEditing" @openAddressLookup="openAddressLookup" />
				<!-- Prevent double submit of form with this null guard-->
			</v-form>
		</template>
	</Dialog>
	<!-- Sibling, not nested: Dialog.vue pins its overlay to z-index 9000, so a picker rendered
	     inside its body would sit under the scrim. The create dialog hides itself instead, the same
	     swap ActionStartRecording does with v-show="!isSubpageOpen". -->
	<v-dialog v-model="isAddressLookupOpen" width="425">
		<AddressLookup v-if="isAddressLookupOpen" @projectSelected="onLocationProjectSelected" @close="closeAddressLookup" />
	</v-dialog>
</template>

<script>
	import api from "../api";
	import Dialog from "./Dialog.vue";
	import { calculateTimeout, getMessageFromError, isEmpty } from "@/helpers/utilities";
	import { couldNot, couldNotLoad } from "@/helpers/userMessages";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import AddressLookup from "./location/AddressLookup.vue";
	import AddressLookupLink from "./location/AddressLookupLink.vue";

	export default {
		components: {
			FieldLabel,
			Dialog,
			AddressLookup,
			AddressLookupLink
		},
		expose: ["open", "editProject"],
		// `locationProjectSelected` is deliberately separate from `created`: a project arriving from the
		// location picker is a "take me to this project" outcome, whereas typing a name is an "add it to my
		// list" outcome. Hosts differ on that (ProjectsPage navigates, RecentProjects stays and refreshes),
		// so the dialog reports which flow produced the project and lets each host decide.
		emits: ["created", "locationProjectSelected"],
		setup() {
			const toast = useToast();
			return { toast };
		},
		data() {
			return {
				dialog: false,
				resolve: null,
				reject: null,
				formRef: false,
				project: {},
				loading: false,
				creating: false,
				isEditing: false,
				editingId: null,
				nameRules: [(v) => (v || "").trim().length > 0 || "Name is required."],
				isFormValid: false,
				isAddressLookupOpen: false
			};
		},
		computed: {
			agreeText() {
				return this.creating ? (this.isEditing ? "Saving" : "Creating") : this.isEditing ? "Save" : "Create Project";
			},
			createButtonDisabled() {
				return this.loading || this.creating;
			},
			shouldAutoFocusProjectName() {
				return isEmpty(this.project.name);
			}
		},
		async mounted() {},
		methods: {
			open(defaultName) {
				this.resetForm();
				this.isEditing = false;
				this.editingId = null;
				this.creating = false;
				this.project = { name: defaultName ?? "" };
				this.dialog = true;
				return new Promise((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			},
			async editProject(id) {
				this.resetForm();
				this.isEditing = true;
				this.editingId = id;
				this.creating = false;
				this.project = {};
				this.loading = true;
				this.dialog = true;
				try {
					const record = await api.getProject(id);
					this.project = { name: record.name || "" };
				} catch (e) {
					this.toast.error(couldNotLoad("the project"));
					this.dialog = false;
					return null;
				} finally {
					this.loading = false;
				}
				return new Promise((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			},
			resetForm() {
				this.$refs.formRef?.reset();
			},
			async agree() {
				try {
					const { valid } = await this.$refs.formRef.validate();
					if (!valid) {
						return;
					}
					this.creating = true;
					let record;
					if (this.isEditing && this.editingId) {
						record = await api.updateProject(this.editingId, this.project);
						this.toast.success("Project updated successfully.");
					} else {
						record = await api.createProject(this.project);
						this.toast.success("Project created successfully.");
						this.$emit("created", record);
					}
					this.resolve(record);
					this.resetForm();
					this.dialog = false;
				} catch (e) {
					// LSP-19484 / LSP-19485: the fallback is passed IN so a server-side failure resolves to
					// this sentence instead of the server's own text, in the one documented opener.
					const message = getMessageFromError(e, couldNot(this.isEditing ? "update the project" : "create the project"));
					this.toast.error(message, {
						timeout: calculateTimeout(message)
					});
					console.error(e, {
						tags: {
							method: "agree",
							file: "CreateProjectDialog"
						}
					});
				} finally {
					this.creating = false;
				}
			},
			cancel() {
				this.resetForm();
				this.dialog = false;
				this.resolve(null);
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			// Closing the picker returns to the create form rather than dismissing the whole flow --
			// `dialog` is untouched, so the hidden Dialog reappears with the typed name intact.
			closeAddressLookup() {
				this.isAddressLookupOpen = false;
			},
			// AddressLookup owns both outcomes: picking a nearby project and creating one at the
			// current location. Either way this dialog's job is done, so resolve open()'s promise with
			// it. `created` fires only for the create half, so consumers listening for a new row
			// (RecentProjects) are not told the list changed when the user merely selected an
			// existing project.
			onLocationProjectSelected(project, isNewProject) {
				this.isAddressLookupOpen = false;
				if (!project) return;
				if (isNewProject) {
					this.$emit("created", project);
				}
				this.$emit("locationProjectSelected", project, isNewProject);
				this.resetForm();
				this.dialog = false;
				this.resolve?.(project);
			}
		}
	};
</script>
